import { useState } from 'react';
import type { RepKind, TeamCard, Warning } from '@/api/base-types';
import { useBaseMutation, useTeamList } from '@/api/base';
import { api } from '@/api/client';
import { ChoiceField } from '@/components/kit/Controls';
import { TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { Note } from '@/components/kit/Note';
import { FieldHint } from '@/components/kit/OptionList';
import { useToast } from '@/components/kit/Toast';
import { years } from '@/lib/phone';
import { normalizeName } from '@/lib/text';
import { CityField, type CityValue } from '../shared/CityField';
import { ClientPicker, emptyPicker, pickerPayload, pickerReady, type PickerState } from '../shared/ClientPicker';
import { StudioPicker } from '../shared/EntityPickers';
import { REP_KIND_ITEMS } from '../shared/labels';
import { errorText } from '../shared/useUndoable';

/** Команда с тем же названием в том же городе — подсказка о дубле (БЛ 5.4). */
function useTeamTwin(name: string, cityId: string | undefined, exceptId?: string) {
  const list = useTeamList({ q: name, cityId, limit: 5 }, name.trim().length >= 2 && Boolean(cityId));
  return (list.data?.items ?? []).find(
    (t) => t.id !== exceptId && t.city.id === cityId && normalizeName(t.name) === normalizeName(name),
  );
}

/**
 * Новая команда (fNewTeam): название, город команды, студия (или без
 * студии), руководитель с телефоном. Заведённая менеджером — «данные
 * ждут руководителя» (БЛ 5.9).
 */
export function NewTeamModal({
  onClose,
  onCreated,
  studio,
  city: presetCity,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  studio?: { id: string; name: string };
  city?: CityValue;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [city, setCity] = useState<CityValue | null>(presetCity ?? null);
  const [studioValue, setStudio] = useState<{ id: string; name: string } | null>(studio ?? null);
  const [head, setHead] = useState<PickerState>(emptyPicker());
  const twin = useTeamTwin(name, city?.id);

  const create = useBaseMutation(() =>
    api.post<{ id: string; warnings: Warning[] }>('/teams', {
      name,
      cityId: city?.id,
      studioId: studioValue?.id ?? null,
      head: pickerPayload(head, 'phone'),
    }),
  );

  return (
    <Modal
      title="Новая команда"
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Создать команду',
        disabled: !name.trim() || !city || !pickerReady(head, 'phone') || create.isPending,
        onClick: () =>
          create.mutate(undefined, {
            onSuccess: (r) => {
              toast.show('Команда создана');
              onClose();
              onCreated(r.id);
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <div className="row keep">
        <div className="col">
          <TextField label="Название" value={name} onValue={setName} autoFocus />
        </div>
        <div className="col">
          <CityField inModal label="Город команды" hint="где команда числится" value={city} onChange={setCity} />
        </div>
      </div>
      {twin ? (
        <div className="mnote">
          <Note tone="warn">
            <b>Похоже на дубль:</b> команда «{twin.name}» в этом городе уже есть. Создать можно, но проверьте.
          </Note>
        </div>
      ) : null}
      <StudioPicker allowNone value={studioValue} onChange={setStudio} />
      <ClientPicker label="Руководитель" need="phone" value={head} onChange={setHead} />
      <div className="mnote">
        <Note>Команду заводит менеджер — система отметит «данные ждут руководителя».</Note>
      </div>
    </Modal>
  );
}

/**
 * Изменить данные команды: название, город, студия. Переход в другую
 * студию — с сегодняшнего дня, прошлое остаётся за прежней (БЛ 5.11).
 */
export function EditTeamModal({ team, onClose }: { team: TeamCard; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(team.name);
  const [city, setCity] = useState<CityValue | null>(team.city);
  const [studio, setStudio] = useState<{ id: string; name: string } | null>(team.studio);
  const [instagram, setInstagram] = useState(team.instagram ?? '');
  const twin = useTeamTwin(name, city?.id, team.id);
  const moved = (studio?.id ?? null) !== (team.studio?.id ?? null);
  const save = useBaseMutation(() =>
    api.patch(`/teams/${team.id}`, {
      name,
      cityId: city?.id,
      studioId: studio?.id ?? null,
      instagram: instagram.trim() || null,
    }),
  );

  return (
    <Modal
      title="Изменить данные команды"
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Сохранить',
        disabled: !name.trim() || !city || save.isPending,
        onClick: () =>
          save.mutate(undefined, {
            onSuccess: () => {
              toast.show('Данные сохранены');
              onClose();
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <div className="row keep">
        <div className="col">
          <TextField label="Название" value={name} onValue={setName} />
        </div>
        <div className="col">
          <CityField inModal label="Город команды" value={city} onChange={setCity} />
        </div>
      </div>
      {twin ? (
        <div className="mnote">
          <Note tone="warn">
            <b>Похоже на дубль:</b> команда «{twin.name}» в этом городе уже есть.
          </Note>
        </div>
      ) : null}
      <TextField label="Instagram" placeholder="ник без @" value={instagram} onValue={setInstagram} />
      <StudioPicker allowNone value={studio} onChange={setStudio} />
      {moved ? (
        <div className="mnote">
          <Note>
            Команда перейдёт {studio ? `в «${studio.name}»` : 'в «без студии»'} с сегодняшнего дня. Прошлые выступления
            и счета остаются за прежней студией.
          </Note>
        </div>
      ) : null}
      <FieldHint gap>Руководитель меняется отдельно — «⋯» → «Сменить руководителя».</FieldHint>
    </Modal>
  );
}

/** Сменить руководителя (БЛ 5.10): кто был, кто стал — в журнал и ленту. */
export function ChangeHeadModal({ team, onClose }: { team: TeamCard; onClose: () => void }) {
  const toast = useToast();
  const [head, setHead] = useState<PickerState>(emptyPicker());
  const save = useBaseMutation(() => api.post(`/teams/${team.id}/head`, { head: pickerPayload(head, 'phone') }));
  return (
    <Modal
      title={`Сменить руководителя «${team.name}»`}
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Сменить',
        disabled: !pickerReady(head, 'phone') || save.isPending,
        onClick: () =>
          save.mutate(undefined, {
            onSuccess: () => {
              toast.show('Руководитель сменён');
              onClose();
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <p>
        Сейчас руководитель — <b>{team.head.fullName}</b>. Ответственный команды сменится вместе с руководителем,
        смена запишется в журнал.
      </p>
      <ClientPicker label="Новый руководитель" need="phone" value={head} onChange={setHead} exclude={[team.head.id]} />
    </Modal>
  );
}

const ageOf = (iso: string | null): number | null => {
  if (!iso) return null;
  const b = new Date(`${iso}T00:00:00`);
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
  return a;
};

/**
 * Включить в состав (fAddMember): участник с датой рождения;
 * несовершеннолетнему — представитель сразу или позже; повторный
 * вход — новый период (БЛ 5.8).
 */
export function AddMemberModal({
  team,
  activeIds,
  leftIds,
  initial,
  onClose,
}: {
  team: TeamCard;
  activeIds: string[];
  leftIds: string[];
  /** «Завести клиентом» из «Выступали без карточки»: новый клиент с данными из номера */
  initial?: { lastName: string; firstName: string; birthDate: string | null; phone: string };
  onClose: () => void;
}) {
  const toast = useToast();
  const [member, setMember] = useState<PickerState>(() =>
    initial ? { ...emptyPicker(), mode: 'new', ...initial } : emptyPicker(),
  );
  const [rep, setRep] = useState<PickerState>(emptyPicker(true));
  const [kind, setKind] = useState<RepKind>('mother');

  const birth = member.mode === 'find' ? (member.chosen?.birthDate ?? member.birthDate) : member.birthDate;
  const age = ageOf(birth);
  const minor = age !== null && age < 18;
  const rejoin = member.mode === 'find' && member.chosen && leftIds.includes(member.chosen.id);

  const add = useBaseMutation(() =>
    api.post(`/teams/${team.id}/members`, {
      client: pickerPayload(member, 'birth'),
      ...(minor && rep.mode !== 'later' ? { representative: { client: pickerPayload(rep, 'phone'), kind } } : {}),
    }),
  );

  const ready = pickerReady(member, 'birth') && (!minor || pickerReady(rep, 'phone')) && !add.isPending;

  return (
    <Modal
      title={`Включить в состав «${team.name}»`}
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Включить в состав',
        disabled: !ready,
        onClick: () =>
          add.mutate(undefined, {
            onSuccess: () => {
              toast.show('Участник в составе');
              onClose();
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <ClientPicker
        label="Участник"
        need="birth"
        value={member}
        onChange={setMember}
        exclude={activeIds}
        withPhone={Boolean(initial)}
      />
      {rejoin ? (
        <div className="mnote">
          <Note>Уже был в составе и выбыл — откроется новый период с сегодняшнего дня, прошлый останется.</Note>
        </div>
      ) : null}
      {minor ? (
        <>
          <hr className="hair mgap" />
          <div className="mnote">
            <Note tone="warn">
              Участнику {years(age)} — нужен представитель. Его можно добавить сейчас или позже с карточки участника.
            </Note>
          </div>
          <ClientPicker label="Представитель" need="phone" allowLater value={rep} onChange={setRep} />
          {rep.mode !== 'later' ? (
            <ChoiceField label="Кем приходится" items={REP_KIND_ITEMS} value={kind} onChange={setKind} />
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

